import { Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FormHeader from "../../../components/form/FormHeader";
import { Button } from "../../../components/button";
import { useToast } from "../../../components/toast-container";
import { goBackOrGoHome } from "../../../utils/navigation";
import { useGetUserById, useUpdateUser } from "../hooks";
import { UserForm, type UserFormValues } from "../components/user-form";

export default function UpdateUserPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { getUser, loading: fetching, error: fetchError } = useGetUserById();
  const { updateUser, loading: saving, error: saveError } = useUpdateUser();
  const [values, setValues] = useState<UserFormValues>({ email: "", phone_number: "", password: "" });
  useEffect(() => {
    if (!id) return;
    void getUser(id)
      .then((user) => {
        if (!user) return;
        setValues({
          email: user.email ?? "",
          phone_number: user.phone_number ?? "",
          password: "",
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
          country_code: user.country_code ?? "",
          country: user.country ?? "",
          zip_code: user.zip_code ?? "",
          gender: user.gender,
          date_of_birth: user.date_of_birth ?? undefined,
          language: user.language,
          population_id: user.population_id ?? undefined,
          age: user.age ?? undefined,
        });
      })
      .catch(() => undefined);
  }, [getUser, id]);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id) return;
    try {
      await updateUser(id, {
        first_name: values.first_name,
        last_name: values.last_name,
        country_code: values.country_code,
        country: values.country,
        zip_code: values.zip_code,
        gender: values.gender,
        date_of_birth: values.date_of_birth,
        language: values.language,
        phone_number: values.phone_number,
        population_id: values.population_id,
        age: values.age === undefined ? undefined : Number(values.age),
      });
      addToast({ type: "success", title: "Success", message: "User updated successfully" });
      navigate("/users", { replace: true });
    } catch {
      /* hook exposes error */
    }
  };
  if (!id) return null;
  return (
    <div className="min-h-screen bg-background">
      <FormHeader
        onBack={() => goBackOrGoHome(navigate)}
        title="Update user"
        actions={
          <Button
            type="submit"
            form="user-update-form"
            disabled={fetching || saving}
          >
            <Check className="h-4 w-4" />
            Save changes
          </Button>
        }
      />
      <UserForm
        formId="user-update-form"
        values={values}
        onChange={setValues}
        onSubmit={submit}
        error={fetchError || saveError}
        loading={fetching || saving}
        mode="update"
      />
    </div>
  );
}
